import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from 'app/lib/axios';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import type { ListingComment } from 'app/types/listing';
import { listingKeys } from './keys';

export function useListingComments(
  listingId: string | undefined,
  opts: FunctionalUseQueryOptions<ListingComment[]> = {},
) {
  return useQuery<ListingComment[]>({
    queryKey: listingKeys.comments(listingId ?? ''),
    queryFn: () =>
      listingId
        ? http.get<ListingComment[]>(`/listings/${listingId}/comments`).then((r) => r.data)
        : Promise.resolve([]),
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(listingId),
  });
}

export function useCreateListingComment(listingId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      http.post<ListingComment>(`/listings/${listingId}/comments`, { body }).then((r) => r.data),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: listingKeys.comments(listingId) });
      void client.invalidateQueries({ queryKey: listingKeys.detail(listingId) });
      void client.invalidateQueries({ queryKey: listingKeys.lists() });
    },
  });
}

export function useDeleteListingComment(listingId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => http.delete(`/listings/${listingId}/comments/${commentId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: listingKeys.comments(listingId) });
      void client.invalidateQueries({ queryKey: listingKeys.detail(listingId) });
      void client.invalidateQueries({ queryKey: listingKeys.lists() });
    },
  });
}
